import { NavLink, useLocation } from 'react-router-dom';
import { cn } from '../../lib/utils';
import {
  LayoutDashboard,
  ShoppingCart,
  Package,
  Users,
  CreditCard,
  Warehouse,
  Calculator,
  FileText,
  BookOpen,
  Database,
  Wifi,
  MapPin,
  Trophy,
  Crown,
  FlaskConical,
  Building2,
  Settings,
  RefreshCw,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { Button } from '../ui/button';

const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
  { icon: ShoppingCart, label: 'Sales', path: '/sales' },
  { icon: Package, label: 'Products', path: '/products' },
  { icon: Users, label: 'Customers', path: '/customers' },
  { icon: CreditCard, label: 'Payments', path: '/payments' },
  { icon: Warehouse, label: 'Inventory', path: '/inventory' },
  { icon: Calculator, label: 'Accounting', path: '/accounting' },
  { icon: Wifi, label: 'UnTill', path: '/untill' },
  { icon: MapPin, label: 'Locations', path: '/locations' },
  { icon: Trophy, label: 'Compare', path: '/compare' },
  { icon: Building2, label: 'Destinations', path: '/destinations' },
  { icon: BookOpen, label: 'QuickBooks', path: '/quickbooks' },
  { icon: Database, label: 'Odoo', path: '/odoo' },
  { icon: FlaskConical, label: 'Dry Run', path: '/dry-run' },
  { icon: FileText, label: 'Logs', path: '/logs' },
  { icon: Settings, label: 'Settings', path: '/settings' },
];

import { useAuth } from '../../context/AuthContext';
const adminItem = { icon: Crown, label: 'Super Admin', path: '/admin' };

export const Sidebar = ({ collapsed, onToggle, mobileOpen, onMobileClose }) => {
  const location = useLocation();
  const { user } = useAuth();
  const items = user?.is_super_admin ? [...navItems, adminItem] : navItems;

  return (
    <aside
      className={cn(
        "fixed left-0 top-0 z-40 h-screen bg-slate-950 border-r border-slate-800 transition-transform duration-300 lg:transition-all",
        // Mobile: drawer that slides in/out. Desktop: always visible, mini or full.
        mobileOpen ? "translate-x-0" : "-translate-x-full",
        "lg:translate-x-0",
        collapsed ? "lg:w-[70px] w-[260px]" : "w-[260px]"
      )}
      data-testid="sidebar"
    >
      {/* Logo */}
      <div className={cn("h-16 flex items-center justify-between px-4 border-b border-slate-800", collapsed && "lg:justify-center lg:px-2")}>
        <div className={cn("flex items-center gap-3 overflow-hidden", collapsed && "lg:hidden")}>
          <div className="w-9 h-9 rounded-lg bg-blue-500 flex items-center justify-center flex-shrink-0">
            <RefreshCw className="w-5 h-5 text-white" />
          </div>
          <span className={cn(
            "text-lg font-bold font-heading text-white tracking-tight whitespace-nowrap",
            collapsed && "lg:hidden"  // hide label only on desktop-mini
          )}>
            SyncPoint
          </span>
        </div>
        {/* Desktop mini/full toggle */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggle}
          className="h-8 w-8 text-slate-400 hover:text-white hover:bg-slate-800 hidden lg:inline-flex"
          data-testid="sidebar-toggle-btn"
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </Button>
        {/* Mobile close */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onMobileClose}
          className="h-8 w-8 text-slate-400 hover:text-white hover:bg-slate-800 lg:hidden"
          data-testid="sidebar-mobile-close-btn"
          aria-label="Close menu"
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
      </div>

      {/* Navigation */}
      <nav className="p-3 space-y-1 overflow-y-auto max-h-[calc(100vh-64px)]" data-testid="sidebar-nav">
        {items.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={onMobileClose}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
                isActive
                  ? "bg-blue-500/10 text-blue-400 border border-blue-500/20"
                  : "text-slate-400 hover:text-white hover:bg-slate-800/50"
              )}
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <item.icon className={cn("w-5 h-5 flex-shrink-0", isActive && "text-blue-400")} />
              <span className={cn(
                "font-medium text-sm whitespace-nowrap",
                collapsed && "lg:hidden"  // hide label only on desktop-mini
              )}>
                {item.label}
              </span>
            </NavLink>
          );
        })}
      </nav>
    </aside>
  );
};
